import { FiCloud } from 'react-icons/fi';

export const secondConditionalData = {
  courseId: 'second-conditional',
  courseType: 'grammar',
  title: 'Second Conditional',
  icon: FiCloud,
  overview: {
    impact: 'Discuss hypothetical or unlikely situations',
    focus: 'If + past simple, would + base verb',
    uses: 'Speaking Part 3, Writing Task 2',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Structure',
      content: `<p><strong>If + past simple, would + base verb</strong></p>`,
      examples: [
        {
          label: 'Hypothetical',
          text: 'If I had more money, I would travel the world.'
        },
        {
          label: 'Unlikely',
          text: 'If I won the lottery, I would buy a house.'
        },
        {
          label: 'Advice',
          text: 'If I were you, I would study harder.'
        }
      ],
      warning: 'Use \'were\' (not \'was\') with I/he/she/it in formal English: \'If I were rich...\''
    },
    {
      title: 'First vs Second Conditional',
      content: `<p>The key difference is about likelihood:</p>`,
      table: {
        headers: [
          'First (likely)',
          'Second (unlikely/imaginary)'
        ],
        rows: [
          [
            'If I get the job, I\'ll move.',
            'If I got the job, I\'d move.'
          ],
          [
            'If it rains, we\'ll stay inside.',
            'If it rained on Mars, we\'d be amazed.'
          ],
          [
            'If she studies, she\'ll pass.',
            'If she studied more, she\'d get band 9.'
          ]
        ]
      }
    },
    {
      title: 'Using in IELTS',
      content: `<p>The second conditional is excellent for Speaking Part 3:</p>`,
      points: [
        'Hypothetical scenarios: \'If the government invested more in education, literacy rates would improve.\'',
        'Giving opinions diplomatically: \'If I were the manager, I would change the policy.\'',
        'Imagining changes: \'If people used less plastic, the oceans would be cleaner.\''
      ]
    }
  ],
  exercises: [
    {
      title: 'First or Second Conditional?',
      type: 'select',
      instructions: 'Choose the correct form based on likelihood.',
      questions: [
        {
          text: 'If I ___ (be) taller, I would play basketball. (impossible change)',
          options: [
            'were',
            'am',
            'will be'
          ],
          answer: 'were',
          explanation: 'Impossible/hypothetical → second conditional with \'were\'.'
        },
        {
          text: 'If it ___ (rain) tomorrow, I\'ll take an umbrella. (likely)',
          options: [
            'rains',
            'rained',
            'would rain'
          ],
          answer: 'rains',
          explanation: 'Likely situation → first conditional with present simple.'
        },
        {
          text: 'If I ___ (have) wings, I ___ (fly). (imaginary)',
          options: [
            'had, would fly',
            'have, will fly',
            'had, will fly'
          ],
          answer: 'had, would fly',
          explanation: 'Imaginary → second conditional: past simple + would.'
        }
      ]
    }
  ],
  tips: [
    'Second conditional = unlikely/imaginary situations',
    'Use \'were\' for all subjects in formal English: \'If I were, If she were\'',
    'First conditional = likely; Second conditional = unlikely/imaginary',
    'Excellent for Part 3 hypothetical discussions'
  ],
  dailyPractice: [
    'Practice second conditional concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Second Conditional: if + past simple, would + base verb. For hypothetical/unlikely situations. \'If I were\' for formal usage. Key for IELTS Speaking Part 3.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...secondConditionalData,
      title: secondConditionalData.title + ' (Foundations)',
      overview: { ...secondConditionalData.overview, duration: '15-20 minutes' },
      sections: secondConditionalData.sections.slice(0, Math.max(2, Math.ceil(secondConditionalData.sections.length / 2))),
      exercises: secondConditionalData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...secondConditionalData,
      title: secondConditionalData.title + ' (Advanced)',
      overview: { ...secondConditionalData.overview, duration: '30-35 minutes' },
      sections: secondConditionalData.sections,
      exercises: secondConditionalData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(secondConditionalData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return secondConditionalData;
}
